import type { CanvasGroupGeometry } from './types';

export interface SliceResult {
  monitorName: string;
  stationId: string | null;
  blob: Blob;
}

/**
 * Slice a source video into per-monitor clips based on canvas group geometry.
 *
 * For each monitor slice, creates a canvas at the monitor's native resolution,
 * plays the video frame-by-frame, crops the relevant region, and records
 * via MediaRecorder → WebM blob.
 *
 * Returns one blob per monitor in the group.
 */
export async function sliceVideo(
  videoFile: File,
  geo: CanvasGroupGeometry,
  onProgress?: (pct: number) => void,
): Promise<SliceResult[]> {
  // Load the video element
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  const videoUrl = URL.createObjectURL(videoFile);
  video.src = videoUrl;

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('Failed to load video'));
  });

  const srcW = video.videoWidth;
  const srcH = video.videoHeight;
  const duration = video.duration;

  // Scale factors from source video to virtual strip coords
  const scaleX = srcW / geo.renderWidth;
  const scaleY = srcH / geo.renderHeight;

  // Process each slice sequentially
  const results: SliceResult[] = [];

  for (let i = 0; i < geo.slices.length; i++) {
    const slice = geo.slices[i];
    onProgress?.(i / geo.slices.length * 100);

    // Output at native monitor resolution
    const outW = slice.nativeW;
    const outH = slice.nativeH;

    // Source crop region in video pixel coords
    const cropX = slice.x * scaleX;
    const cropY = slice.y * scaleY;
    const cropW = slice.w * scaleX;
    const cropH = slice.h * scaleY;

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d')!;

    // Set up recording
    const stream = canvas.captureStream(30);
    const recorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 8_000_000,
    });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

    const blobPromise = new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: 'video/webm' }));
    });

    recorder.start();

    // Play video and draw cropped frames
    video.currentTime = 0;
    await new Promise<void>((resolve) => { video.onseeked = () => resolve(); });

    const fps = 30;
    const totalFrames = Math.ceil(duration * fps);

    for (let f = 0; f < totalFrames; f++) {
      video.currentTime = f / fps;
      await new Promise<void>((resolve) => { video.onseeked = () => resolve(); });

      ctx.clearRect(0, 0, outW, outH);
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

      // Pace frames with requestAnimationFrame to keep UI responsive
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      onProgress?.((i / geo.slices.length + (f / totalFrames) / geo.slices.length) * 100);
    }

    recorder.stop();
    stream.getTracks().forEach((t) => t.stop());

    const blob = await blobPromise;
    results.push({
      monitorName: slice.name,
      stationId: slice.monitorRef.stationId,
      blob,
    });
  }

  URL.revokeObjectURL(videoUrl);
  onProgress?.(100);
  return results;
}

/** Download a single slice result as a WebM file */
export function downloadSlice(result: SliceResult) {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${result.monitorName.replace(/\s+/g, '-').toLowerCase()}.webm`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Download all slice results */
export function downloadAllSlices(results: SliceResult[]) {
  for (const r of results) downloadSlice(r);
}
